import { parseMoney, type Money } from '@mybooks/ledger';

// Formatting for practice screens, fixed per country (PRACTICE-DESIGN.md 8): South Africa shows
// "R18,000.00" and "25 Nov", never the default en-ZA Intl output ("R 18 000,00"), so every screen
// matches. The US pack shows "$18,000.00" and "Nov 25".

export type Country = 'ZA' | 'US';

const SYMBOL: Record<Country, string> = { ZA: 'R', US: '$' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const group = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * "R18,000.00", or with `whole` "R18,000" for summaries. Rounds half away from zero to cents (or
 * to whole units). Takes Money or the API's decimal string.
 */
export function formatAmount(value: Money | string | null | undefined, country: Country, opts: { whole?: boolean } = {}): string {
  const amount = typeof value === 'bigint' ? value : value ? parseMoney(value) : 0n;
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const step = opts.whole ? 10_000n : 100n;
  const rounded = ((abs + step / 2n) / step) * step;
  const whole = rounded / 10_000n;
  const cents = (rounded % 10_000n) / 100n;
  const text = `${SYMBOL[country]}${group(whole.toString())}${opts.whole ? '' : `.${cents.toString().padStart(2, '0')}`}`;
  return negative ? `-${text}` : text;
}

function parts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return { y: y ?? 1970, m: (m ?? 1) - 1, d: d ?? 1 };
}

/** "25 Nov" (ZA) or "Nov 25" (US); with `year`, "25 Nov 2026" or "Nov 25, 2026". */
export function formatDay(iso: string | null | undefined, country: Country, opts: { year?: boolean } = {}): string {
  if (!iso) return '';
  const { y, m, d } = parts(iso);
  const month = MONTHS[m] ?? '';
  if (country === 'US') return opts.year ? `${month} ${d}, ${y}` : `${month} ${d}`;
  return opts.year ? `${d} ${month} ${y}` : `${d} ${month}`;
}

/** The short month name for 1 to 12. */
export const monthName = (month: number) => MONTHS[(month - 1 + 12) % 12] ?? '';

/**
 * The financial year a date falls in, named by the calendar year it ends in: with a February year
 * end, March 2026 to February 2027 is "FY2027".
 */
export function financialYear(iso: string, yearEndMonth: number): { label: string; start: string; end: string; endYear: number } {
  const { y, m } = parts(iso);
  const endYear = m + 1 > yearEndMonth ? y + 1 : y;
  const startMonth = (yearEndMonth % 12) + 1;
  const startYear = startMonth === 1 ? endYear : endYear - 1;
  const lastDay = new Date(Date.UTC(endYear, yearEndMonth, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    label: `FY${endYear}`,
    start: `${startYear}-${pad(startMonth)}-01`,
    end: `${endYear}-${pad(yearEndMonth)}-${pad(lastDay)}`,
    endYear,
  };
}

/** Whole days from one ISO date to another. */
export const daysFrom = (from: string, to: string) =>
  Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000);

/** "1 day", "3 days". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Updated today at 07:00", "updated yesterday at 22:15", "updated 3 Nov at 07:00". */
export function updatedWords(at: string, now: Date, country: Country): string {
  const when = new Date(at);
  const time = `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`;
  const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const days = daysFrom(localIso(when), localIso(now));
  const day = days <= 0 ? 'today' : days === 1 ? 'yesterday' : formatDay(localIso(when), country);
  return `updated ${day} at ${time}`;
}
